import { useState, useEffect, useCallback } from "react";

import { API } from "../config";
const FONT = "'DM Sans', sans-serif";
const MONO = "'IBM Plex Mono', monospace";

// ── Label per tipo mail ─────────────────────────────────────────
const TIPO_LABEL = {
  "mail_errori":            { icon: "⚠️", label: "Mail Errori",            colore: "#EF4444" },
  "mail_programma":         { icon: "📨", label: "Mail Programma",         colore: "#3B82F6" },
  "mail_aggiornamento":     { icon: "📨", label: "Mail Aggiornamento",     colore: "#3B82F6" },
  "mail_bilancio":          { icon: "📊", label: "Mail Bilancio",          colore: "#8B5CF6" },
  // TASK 17 Strato D — Mail di sistema vigile periodo off-season (D400, D402, D403)
  "mail_pausa_estiva":      { icon: "🌙", label: "Pausa estiva",           colore: "#6366F1" },
  "mail_stagione_al_via":   { icon: "🚀", label: "Stagione al via",        colore: "#10B981" },
  "mail_allarme_date":      { icon: "⚠️", label: "Allarme date stagione",  colore: "#F59E0B" },
  "mail_watchdog":          { icon: "🛡️", label: "Controllo settimanale",  colore: "#06B6D4" },
};
function getTipoInfo(tipo) {
  return TIPO_LABEL[tipo] || { icon: "📧", label: tipo || "—", colore: "#64748B" };
}

// ── Formatta timestamp ──────────────────────────────────────────
function fmtTimestamp(ts) {
  if (!ts) return "—";
  try {
    const s = String(ts);
    const [datePart, timePart] = s.split("T").length > 1 ? s.split("T") : s.split(" ");
    const [y, m, d] = datePart.split("-");
    const ora = timePart ? timePart.substring(0, 5) : "";
    return `${d}/${m}/${y}${ora ? " " + ora : ""}`;
  } catch { return ts; }
}


// ── Stili ───────────────────────────────────────────────────────
const stili = {
  pagina: { padding: "24px 32px", fontFamily: FONT, background: "#FAFBFC", minHeight: "100vh" },
  titolo: { fontSize: 22, fontWeight: 700, color: "#1E293B", margin: "0 0 4px 0" },
  card:   { background: "#fff", borderRadius: 10, border: "1px solid #E2E8F0", marginBottom: 16 },
  btn:    { fontFamily: FONT, fontSize: 13, fontWeight: 600, padding: "7px 16px",
            borderRadius: 6, border: "none", cursor: "pointer", transition: "background 0.15s" },
};

// ── Fetch mail dal backend ──────────────────────────────────────
async function fetchMailInviate() {
  const resp = await fetch(`${API}/mail/inviate`);
  if (!resp.ok) throw new Error(`Errore ${resp.status}`);
  return await resp.json();
}

async function segnaLetta(id) {
  await fetch(`${API}/mail/inviate/${id}/letta`, { method: "POST" });
}

async function eliminaMail(id) {
  await fetch(`${API}/mail/inviate/${id}/elimina`, { method: "POST" });
}

async function ripristinaMail(id) {
  await fetch(`${API}/mail/inviate/${id}/ripristina`, { method: "POST" });
}

async function segnaNonLetta(id) {
  await fetch(`${API}/mail/inviate/${id}/non-letta`, { method: "POST" });
}


export default function MailSistema({ mailApertaId }) {
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState("");
  const [mailSelezionata, setMailSelezionata] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [ripristinando, setRipristinando] = useState(false);
  const [segnandoNonLetta, setSegnandoNonLetta] = useState(false);

  const [filtroLette, setFiltroLette] = useState("tutte");

  // ── Carica mail ─────────────────────────────────────────────
  const carica = useCallback(async () => {
    setLoading(true);
    setErrore("");
    try {
      const data = await fetchMailInviate();
      const lista = Array.isArray(data) ? data : (data.mails || []);
      // Se c'è una mail da aprire, segnala letta subito nella lista
      let primaId = null;
      if (mailApertaId) {
        const target = lista.find(m => m.id === Number(mailApertaId) || m.log_id === Number(mailApertaId));
        if (target) primaId = target.id;
      } else if (lista.length > 0) {
        primaId = lista[0].id;
      }
      // Aggiorna letta nella lista locale prima di settarla
      const listaAggiornata = lista.map(m =>
        m.id === primaId ? { ...m, letta: true } : m
      );
      setMails(listaAggiornata);
      // Apri la mail
      if (primaId) {
        const mailDaAprire = listaAggiornata.find(m => m.id === primaId);
        if (mailDaAprire) apriMail(mailDaAprire);
      }
    } catch (e) {
      setErrore("Impossibile caricare le mail.");
    } finally {
      setLoading(false);
    }
  }, [mailApertaId]);

  useEffect(() => { carica(); }, [carica]);

  // ── Apri mail (segna letta + carica corpo) ──────────────────
  const apriMail = async (mail) => {
    setMailSelezionata({ ...mail, letta: true });
    // Aggiorna lista locale
    setMails(prev => prev.map(m =>
      m.id === mail.id ? { ...m, letta: true } : m
    ));
    // Carica corpo_html completo
    try {
      const resp = await fetch(`${API}/mail/inviate/${mail.id}`);
      if (resp.ok) {
        const completa = await resp.json();
        setMailSelezionata({ ...completa, letta: true });
      }
    } catch {}
    // Segna letta nel DB
    try { await segnaLetta(mail.id); } catch {}
  };

  // ── Elimina mail ─────────────────────────────────────────────
  const handleElimina = async () => {
    if (!mailSelezionata) return;
    setEliminando(true);
    try {
      await eliminaMail(mailSelezionata.id);
      // Segna come eliminata nella lista locale (non rimuovere — serve per il Cestino)
      setMails(prev => prev.map(m =>
        m.id === mailSelezionata.id ? { ...m, eliminata: true } : m
      ));
      // Apri la mail successiva nella posta in arrivo
      const visibili = mails.filter(m => !m.eliminata && m.id !== mailSelezionata.id);
      if (visibili.length > 0) {
        apriMail(visibili[0]);
      } else {
        setMailSelezionata(null);
      }
    } catch {}
    setEliminando(false);
  };

  // ── Ripristina mail dal cestino ─────────────────────────────
  const handleRipristina = async () => {
    if (!mailSelezionata) return;
    setRipristinando(true);
    try {
      await ripristinaMail(mailSelezionata.id);
      setMails(prev => prev.map(m =>
        m.id === mailSelezionata.id ? { ...m, eliminata: false } : m
      ));
      setMailSelezionata(null);
    } catch {}
    setRipristinando(false);
  };

  // ── Segna come non letta ─────────────────────────────────────
  const handleSegnaNonLetta = async () => {
    if (!mailSelezionata) return;
    setSegnandoNonLetta(true);
    try {
      await segnaNonLetta(mailSelezionata.id);
      setMails(prev => prev.map(m =>
        m.id === mailSelezionata.id ? { ...m, letta: false } : m
      ));
      setMailSelezionata(prev => prev ? { ...prev, letta: false } : prev);
    } catch {}
    setSegnandoNonLetta(false);
  };

  const mailVisibili = mails.filter(m => {
    if (filtroLette === "cestino") return m.eliminata;
    if (m.eliminata) return false;
    if (filtroLette === "non_lette") return !m.letta;
    if (filtroLette === "lette") return m.letta;
    return true;
  });
  const nonLette = mailVisibili.filter(m => !m.letta).length;


  // ── Render ───────────────────────────────────────────────────
  return (
    <div style={stili.pagina}>
      <h1 style={stili.titolo}>📨 Mail di sistema</h1>
      <p style={{ fontSize: 13, color: "#64748B", marginBottom: 24 }}>
        Posta in arrivo delle notifiche operative della piattaforma.
      </p>

      {errore && (
        <div style={{ background: "#FFEBEE", border: "1px solid #FFCDD2",
                      borderRadius: 8, padding: "12px 16px", marginBottom: 16,
                      color: "#C62828", fontSize: 13, fontFamily: FONT }}>
          {errore}
        </div>
      )}

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>

        {/* ── COLONNA SINISTRA — lista mail ── */}
        <div style={{ ...stili.card, width: 320, flexShrink: 0, overflow: "hidden" }}>
          {/* Header lista */}
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #E2E8F0",
                        display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>
              Posta in arrivo
            </span>
            {nonLette > 0 && (
              <span style={{ background: "#3B82F6", color: "#fff", fontSize: 11,
                             fontWeight: 700, padding: "2px 8px", borderRadius: 12 }}>
                {nonLette}
              </span>
            )}
          </div>
          {/* Filtro lette/non lette */}
          <div style={{ display: "flex", borderBottom: "1px solid #E2E8F0" }}>
            {[
              { id: "tutte",     label: "Tutte" },
              { id: "non_lette", label: "Non lette" },
              { id: "lette",     label: "Lette" },
              { id: "cestino",   label: "🗑 Cestino" },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFiltroLette(f.id)}
                style={{
                  flex: 1, padding: "7px 4px", border: "none", cursor: "pointer",
                  fontFamily: FONT, fontSize: 12, fontWeight: filtroLette === f.id ? 700 : 400,
                  color: filtroLette === f.id ? "#2563EB" : "#64748B",
                  background: filtroLette === f.id ? "#EFF6FF" : "#fff",
                  borderBottom: filtroLette === f.id ? "2px solid #2563EB" : "2px solid transparent",
                  transition: "all 0.15s",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Lista */}
          {loading ? (
            <div style={{ padding: 32, textAlign: "center", color: "#94A3B8",
                          fontSize: 13, fontFamily: FONT }}>
              Caricamento...
            </div>
          ) : mailVisibili.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "#94A3B8",
                          fontSize: 13, fontFamily: FONT }}>
              Nessuna mail disponibile
            </div>
          ) : (
            <div style={{ maxHeight: "calc(100vh - 180px)", overflowY: "auto" }}>
              {mailVisibili.map(mail => {
                const tipoInfo = getTipoInfo(mail.tipo);
                const isSelezionata = mailSelezionata?.id === mail.id;
                const isNonLetta = !mail.letta;
                return (
                  <div
                    key={mail.id}
                    onClick={() => apriMail(mail, null)}
                    style={{
                      padding: "12px 16px", cursor: "pointer",
                      borderBottom: "1px solid #F1F5F9",
                      background: isSelezionata ? "#EFF6FF"
                                : isNonLetta ? "#FAFBFF" : "#fff",
                      borderLeft: isSelezionata ? "3px solid #3B82F6"
                                : isNonLetta ? "3px solid #93C5FD" : "3px solid transparent",
                      transition: "background 0.1s",
                    }}
                  >
                    {/* Riga 1: tipo + ora */}
                    <div style={{ display: "flex", justifyContent: "space-between",
                                  alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: isNonLetta ? 700 : 500,
                                     color: tipoInfo.colore }}>
                        {tipoInfo.icon} {tipoInfo.label}
                      </span>
                      <span style={{ fontSize: 11, color: "#94A3B8", fontFamily: MONO }}>
                        {fmtTimestamp(mail.data_invio)}
                      </span>
                    </div>
                    {/* Riga 2: oggetto */}
                    <div style={{ fontSize: 12, color: "#334155",
                                  fontWeight: isNonLetta ? 600 : 400,
                                  whiteSpace: "nowrap", overflow: "hidden",
                                  textOverflow: "ellipsis" }}>
                      {mail.oggetto || "—"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>


        {/* ── COLONNA DESTRA — visualizzatore mail ── */}
        <div style={{ ...stili.card, flex: 1, overflow: "hidden", minWidth: 0 }}>
          {!mailSelezionata ? (
            <div style={{ padding: 48, textAlign: "center", color: "#94A3B8",
                          fontSize: 13, fontFamily: FONT }}>
              Seleziona una mail per visualizzarla
            </div>
          ) : (
            <>
              {/* Header mail */}
              <div style={{ padding: "14px 20px", borderBottom: "1px solid #E2E8F0" }}>
                {/* Oggetto */}
                <div style={{ fontSize: 16, fontWeight: 700, color: "#1E293B",
                              marginBottom: 10 }}>
                  {mailSelezionata.oggetto || "—"}
                </div>
                {/* Data invio */}
                <div style={{ fontSize: 11, color: "#94A3B8", fontFamily: MONO }}>
                  {fmtTimestamp(mailSelezionata.data_invio)}
                </div>
                {/* Tasti azione */}
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  {!mailSelezionata.eliminata && (
                    <>
                      <button
                        style={{ ...stili.btn, background: "#FEF2F2", color: "#DC2626",
                                 border: "1px solid #FECACA", fontSize: 12, padding: "5px 12px" }}
                        onClick={handleElimina}
                        disabled={eliminando}
                      >
                        🗑 {eliminando ? "Eliminazione..." : "Elimina"}
                      </button>
                      <button
                        style={{ ...stili.btn, background: "#F1F5F9", color: "#475569",
                                 border: "1px solid #CBD5E1", fontSize: 12, padding: "5px 12px" }}
                        onClick={handleSegnaNonLetta}
                        disabled={segnandoNonLetta || !mailSelezionata.letta}
                      >
                        ✉️ {segnandoNonLetta ? "..." : "Segna come non letta"}
                      </button>
                    </>
                  )}
                  {mailSelezionata.eliminata && (
                    <button
                      style={{ ...stili.btn, background: "#EFF6FF", color: "#2563EB",
                               border: "1px solid #BFDBFE", fontSize: 12, padding: "5px 12px" }}
                      onClick={handleRipristina}
                      disabled={ripristinando}
                    >
                      ↩️ {ripristinando ? "Ripristino..." : "Ripristina"}
                    </button>
                  )}
                </div>
              </div>

              {/* Corpo mail — renderizzato come HTML originale */}
              <div style={{ padding: "20px 24px", overflowX: "auto" }}>
                {mailSelezionata.corpo_html ? (
                  <iframe
                    srcDoc={mailSelezionata.corpo_html}
                    style={{ width: "100%", minHeight: 500, border: "none" }}
                    title="corpo-mail"
                    sandbox="allow-same-origin"
                  />
                ) : (
                  <div style={{ color: "#94A3B8", fontSize: 13, fontFamily: FONT }}>
                    Corpo mail non disponibile
                  </div>
                )}
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
